import React, { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Info, Package } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import Breadcrumbs, { toLdItems } from "@/components/Breadcrumbs";
import { SITE_URL, buildBreadcrumbLd } from "@/lib/seo";
import { useSeoT, PAGE_META } from "@/lib/seoPagesI18n";
import { useToolsT } from "@/lib/toolsI18n";
import { HOUSING_KEYS, PERSON_KEYS, CLUTTER_KEYS, EXTRA_KEYS, computeBoxes } from "@/lib/toolsConfig";
import { useProject, track } from "@/lib/projectStore";

const H2 = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

export default function CalculateurCartons() {
  const navigate = useNavigate();
  const { t: st, lang } = useSeoT();
  const T = useToolsT(lang);
  const B = T.boxes;
  const meta = (PAGE_META[lang] || PAGE_META.fr).cartons;
  const { project, update } = useProject(true);
  const saved = project?.boxes?.inputs;
  const [housing, setHousing] = useState(saved?.housing || "3p");
  const [persons, setPersons] = useState(saved?.persons || 2);
  const [clutter, setClutter] = useState(saved?.clutter || "normal");
  const [extras, setExtras] = useState(saved?.extras || []);

  React.useEffect(() => { track("box_calculator_start"); }, []);

  const result = useMemo(() => computeBoxes({ housing, persons, clutter, extras }), [housing, persons, clutter, extras]);

  React.useEffect(() => {
    update((p) => { p.boxes = { inputs: { housing, persons, clutter, extras }, result }; return p; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [housing, persons, clutter, extras]);

  const toggleExtra = (e) => setExtras((cur) => cur.includes(e) ? cur.filter((x) => x !== e) : [...cur, e]);
  const bc = [{ name: st.bc.home, path: "/" }, { name: st.bc.outils, path: "/outils" }, { name: meta.h1, path: "/outils/calculateur-cartons" }];
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "WebApplication", name: meta.h1, applicationCategory: "UtilitiesApplication", operatingSystem: "Web", url: `${SITE_URL}/outils/calculateur-cartons`, offers: { "@type": "Offer", price: "0", priceCurrency: "CHF" }, publisher: { "@id": `${SITE_URL}/#organization` } },
    buildBreadcrumbLd(toLdItems(bc)),
  ];
  const goQuote = () => { track("box_calculator_complete", { boxes: result.total }); navigate("/devis?service=move"); };
  const Seg = ({ options, value, onChange, testid, render }) => (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => (
        <button key={o} onClick={() => onChange(o)} className={`rounded-full px-4 py-2.5 text-sm font-bold transition-all ${value === o ? "bg-[#2D5BFF] text-white" : "bg-[#F7F7F8] text-[#4B5563] hover:bg-gray-200"}`} data-testid={`${testid}-${o}`}>{render(o)}</button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-white text-[#0A0F2C]" data-testid="calc-cartons">
      <Seo title={meta.title} description={meta.desc} path="/outils/calculateur-cartons" jsonLd={jsonLd} />
      <Header />
      <section className="relative bg-[#0A0F2C] text-white overflow-hidden pt-28 md:pt-36 pb-12 md:pb-16 px-5 sm:px-8 md:px-10 lg:px-14">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at top right, rgba(45,91,255,0.4), transparent 55%)" }} />
        <div className="relative mx-auto" style={{ maxWidth: "1080px" }}>
          <div className="mb-6"><Breadcrumbs items={bc} variant="dark" /></div>
          <h1 className="font-extrabold text-3xl md:text-5xl tracking-tighter leading-[1.02] max-w-2xl" style={H2}>{meta.h1}</h1>
          <p className="mt-4 text-white/80 text-base md:text-lg max-w-2xl">{meta.intro}</p>
        </div>
      </section>

      <section className="py-8 md:py-14 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8" style={{ maxWidth: "1080px" }}>
          <div className="flex flex-col gap-7">
            <div>
              <div className="font-bold text-sm mb-3" style={H2}>{B.housingLabel}</div>
              <Seg options={HOUSING_KEYS} value={housing} onChange={setHousing} testid="box-housing" render={(o) => B.housing[o]} />
            </div>
            <div>
              <div className="font-bold text-sm mb-3" style={H2}>{B.personsLabel}</div>
              <Seg options={PERSON_KEYS} value={persons} onChange={setPersons} testid="box-persons" render={(o) => (o === 5 ? "5+" : String(o))} />
            </div>
            <div>
              <div className="font-bold text-sm mb-3" style={H2}>{B.clutterLabel}</div>
              <Seg options={CLUTTER_KEYS} value={clutter} onChange={setClutter} testid="box-clutter" render={(o) => B.clutter[o]} />
            </div>
            <div>
              <div className="font-bold text-sm mb-1" style={H2}>{B.extrasLabel}</div>
              <div className="text-xs text-[#9CA3AF] mb-3">{B.extrasHint}</div>
              <div className="flex flex-wrap gap-2" data-testid="box-extras">
                {EXTRA_KEYS.map((e) => (
                  <button key={e} onClick={() => toggleExtra(e)} className={`rounded-full px-4 py-2.5 text-sm font-bold transition-all ${extras.includes(e) ? "bg-[#FFD84D] text-[#0A0F2C]" : "bg-[#F7F7F8] text-[#4B5563] hover:bg-gray-200"}`} data-testid={`box-extra-${e}`}>{B.extras[e]}</button>
                ))}
              </div>
            </div>
          </div>

          <aside className="lg:sticky lg:top-24 h-fit">
            <div className="rounded-3xl bg-[#F7F7F8] p-7">
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{B.resultTitle}</div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-extrabold text-4xl md:text-5xl tabular-nums" data-testid="box-range">{result.min}–{result.max}</span>
                <span className="font-bold text-lg text-[#6B7280]">{B.boxesUnit}</span>
              </div>
              <div className="mt-5">
                <div className="text-xs font-bold text-[#6B7280] mb-2">{B.breakdownTitle}</div>
                <div className="flex flex-col gap-2">
                  {Object.entries(result.breakdown).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-sm" data-testid={`box-bd-${k}`}>
                      <span className="inline-flex items-center gap-2"><Package size={14} className="text-[#2D5BFF]" /> {B.types[k]}</span>
                      <span className="font-bold tabular-nums">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={goQuote} className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0A0F2C] hover:bg-[#1a2050] text-white px-6 py-4 font-extrabold text-sm transition-all" data-testid="box-cta-quote">{B.cta} <ArrowRight size={15} /></button>
              <p className="mt-4 flex items-start gap-2 text-[11px] text-[#9CA3AF]"><Info size={13} className="shrink-0 mt-0.5" /> {B.note}</p>
            </div>
            <div className="mt-5 rounded-3xl border border-gray-100 p-6">
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{B.relatedTitle}</div>
              <div className="mt-3 flex flex-col gap-2 text-sm font-semibold">
                <Link to="/guides/checklist-demenagement-suisse" className="inline-flex items-center gap-1.5 hover:text-[#2D5BFF]">{T.related.checklist} <ArrowRight size={13} /></Link>
                <Link to="/outils/calculateur-volume-demenagement" className="inline-flex items-center gap-1.5 hover:text-[#2D5BFF]">{T.related.volumeGuide} <ArrowRight size={13} /></Link>
                <Link to="/outils" className="inline-flex items-center gap-1.5 hover:text-[#2D5BFF]">{T.related.tools} <ArrowRight size={13} /></Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </div>
  );
}
